import { OoAvailabilityQuery } from 'src/apollo/onlineOrdering'
import ToastProduct from 'src/public/components/online_ordering/ToastProduct'

/**
 * Returns the referrer and referring domain for the current browser session.
 * @param overrideReferrer An optional URL to use to generate referrer and referring domain.
 * @returns {[string | undefined, string | undefined]} A tuple containing the referrer and referring domain, either value undefined if unable to be resolved.
 */
export const getReferrerProperties = (overrideUrl?: string) => {
  try {
    const { href: referrer, hostname: referringDomain } = new URL(
      overrideUrl || document?.referrer || ''
    )
    return [referrer, referringDomain]
  } catch (error) {
    return [undefined, undefined]
  }
}

export type ReferrerChannel =
  | 'OWG'
  | 'Flyer'
  | '3PO QR Code'
  | 'Google Ad'
  | 'SEO'
  | 'Ask Maps'
  | 'Voice Ordering'

/**
 * Determines the referrer channel for the current session based on URL params and document.referrer.
 * Priority order: OWG > Ask Maps > Voice Ordering > Flyer > 3PO QR Code > Google Ad > SEO > undefined
 *
 * @param url - Optional URL string to use instead of window.location.href (for testing)
 * @param referrer - Optional referrer string to use instead of document.referrer (for testing)
 * @returns The referrer channel, or undefined if none detected
 */
export const getReferrerChannel = (
  url?: string,
  referrer?: string
): ReferrerChannel | undefined => {
  try {
    const href =
      url ?? (typeof window !== 'undefined' ? window.location.href : '')
    const docReferrer =
      referrer ?? (typeof document !== 'undefined' ? document.referrer : '')

    const params = new URL(href).searchParams

    if (params.has('rwg_token') || params.get('source') === 'google_feed')
      return 'OWG'
    if (params.get('source') === 'ask_maps') return 'Ask Maps'
    if (params.get('source') === 'voice_ordering') return 'Voice Ordering'
    if (params.get('utm_campaign') === 'marketing-hub-flyer') return 'Flyer'
    if (params.get('utm_campaign') === '3po-receipt-marketing')
      return '3PO QR Code'
    if (params.has('gclid')) return 'Google Ad'
    if (docReferrer && new URL(docReferrer).hostname.includes('google'))
      return 'SEO'

    return undefined
  } catch {
    return undefined
  }
}

/**
 * Determines whether the current session originated from the Google Offers feed
 * based on the presence of `source=google_feed` in the URL.
 *
 * @param url - Optional URL string to use instead of window.location.href (for testing)
 * @returns 'offers' if the session came from the Google Offers feed, undefined otherwise
 */
export const getOwgFeedProperty = (url?: string): 'offers' | undefined => {
  try {
    const href =
      url ?? (typeof window !== 'undefined' ? window.location.href : '')
    const params = new URL(href).searchParams
    return params.get('source') === 'google_feed' ? 'offers' : undefined
  } catch {
    return undefined
  }
}

/**
 * Returns the ToastLocal product for the restaurant.
 * @param restaurant The restaurant data from the OoAvailabilityQuery.
 * @returns If the restaurant is not enabled for online ordering and toast local is enabled,
 * it will return the FreeOOToastLocal product. Otherwise, it will return the ToastLocal product.
 */
export const getToastLocalProduct = (
  restaurant: OoAvailabilityQuery['restaurantV2']
) => {
  if (
    restaurant?.__typename === 'Restaurant' &&
    !restaurant?.onlineOrderingEnabled &&
    restaurant?.toastLocalEnabled
  ) {
    return ToastProduct.FreeOOToastLocal
  }
  return ToastProduct.ToastLocal
}
